import { HelpProvider } from "@/context/HelpContext";
import { NewsEntry } from "@/components/News";
import { SupportEntry } from "@/components/SupportEntry";
import { WorkerTransferNotice } from "@/components/roster/WorkerTransferFlow";
import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import { inboxRootUrl } from "@/lib/inboxNavigation";
import { workerDocumentWorkspace } from "@/lib/workerTabs";
import { hasProcurement } from "@/lib/procurement";
import { hasInvoicePermission } from "@/lib/receivedInvoices";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ObligationNavBadge } from "@/components/obveze/ObligationNavBadge";
import { InboxNavBadge } from "@/components/dokumenti/InboxNavBadge";
import { ScanNotifications } from "@/components/dokumenti/ScanNotifications";
import { PonudaZaInstalaciju } from "@/components/PonudaZaInstalaciju";
import { TrakaReda } from "@/components/TrakaReda";
import { useAuth } from "@/context/AuthContext";
import { Icon, type IconName } from "@/components/ui/icon";
import { Avatar, Dialog } from "@/components/ui/design-system";
import { cn } from "@/lib/utils";
interface Stavka {
  putanja: string;
  naziv: string;
  icon: IconName;
  sekcija?: string;
  ovlast?: [string, string];
}
const STAVKE: Stavka[] = [
  { putanja: "/", naziv: "Početna", icon: "home" },
  { putanja: "/oprema", naziv: "Oprema", icon: "helmet", sekcija: "Tvrtka", ovlast: ["oprema_gradiliste", "read"] },
  { putanja: "/nabava", naziv: "Narudžbe", icon: "clipboard", sekcija: "Tvrtka", ovlast: ["nabava", "read"] },
  { putanja: "/ozo-zahtjevi", naziv: "Zaštitna oprema", icon: "helmet", sekcija: "Tvrtka", ovlast: ["ozo_zahtjev", "read"] },
  { putanja: "/nabava/dobavljaci", naziv: "Dobavljači", icon: "building", sekcija: "Tvrtka", ovlast: ["nabava", "read"] },
  {
    putanja: "/ladica",
    naziv: "Ulazni dokumenti",
    icon: "inbox",
    ovlast: ["dokument", "inbox_read"],
  },
  {
    putanja: "/ladica/unos",
    naziv: "Unos dokumenata",
    icon: "upload",
    ovlast: ["dokument", "inbox_upload"],
  },
  {
    putanja: "/sati",
    naziv: "Evidencija rada",
    icon: "hours",
    ovlast: ["sati", "create"],
  },
  {
    putanja: "/gradilisni-spisak",
    naziv: "Gradilišni popis",
    icon: "helmet",
    ovlast: ["gradilisni_spisak", "read"],
  },
  { putanja: "/ucinak-sati", naziv: "Učinak sati", icon: "hours", ovlast: ["sati", "read"] },
  { putanja: "/sporedni-poslovi", naziv: "Sporedni poslovi", icon: "settings", sekcija: "Administracija", ovlast: ["sifrarnik", "update"] },

  {
    putanja: "/zaposlenici",
    naziv: "Zaposlenici",
    icon: "users",
    sekcija: "Kadrovi",
    ovlast: ["radnik.osnovno", "read"],
  },
  { putanja: "/bolovanja", naziv: "Bolovanja", icon: "clipboard", sekcija: "Kadrovi", ovlast: ["bolovanje", "read"] },
  {
    putanja: "/dozvole/postupci",
    naziv: "Dozvole u postupku",
    icon: "clipboard",
    sekcija: "Kadrovi",
    ovlast: ["dozvola", "read"],
  },
  {
    putanja: "/adrese-za-prijavu",
    naziv: "Adrese za prijavu",
    icon: "clipboard",
    sekcija: "Kadrovi",
    ovlast: ["radnik", "read"],
  },
  {
    putanja: "/dozvole/kontrola",
    naziv: "Kontrola dozvola",
    icon: "clipboard",
    sekcija: "Kadrovi",
    ovlast: ["dozvola", "read"],
  },
  {
    putanja: "/rokovi-i-obveze",
    naziv: "Rokovi i obveze",
    icon: "clipboard",
    sekcija: "Kadrovi",
    ovlast: ["radnik", "read"],
  },
  {
    putanja: "/obveze-tvrtke",
    naziv: "Obveze tvrtke",
    icon: "building",
    sekcija: "Tvrtka",
    ovlast: ["company_obligation", "read"],
  },
  {
    putanja: "/gradilista",
    naziv: "Gradilišta",
    icon: "mapPin",
    sekcija: "Tvrtka",
    ovlast: ["gradiliste", "read"],
  },
  {
    putanja: "/skeneri",
    naziv: "Skeneri",
    icon: "scan",
    sekcija: "Administracija",
    ovlast: ["scanner", "manage"],
  },
  {
    putanja: "/postavke",
    naziv: "Postavke",
    icon: "settings",
    sekcija: "Administracija",
    ovlast: ["auth", "update"],
  },
  {
    putanja: "/korisnici",
    naziv: "Korisnici",
    icon: "settings",
    sekcija: "Administracija",
    ovlast: ["auth", "read"],
  },
  { putanja: "/pomoc/upute", naziv: "Pomoć i podrška", icon: "clipboard", sekcija: "support" },
];
function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  useEffect(() => {
    if (open)
      root.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
  }, [open]);
  return (
    <div
      className="avm-topbar__user"
      ref={root}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setOpen(false);
          button.current?.focus();
        }
        if (open && ["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
          e.preventDefault();
          const items = [
            ...root.current!.querySelectorAll<HTMLElement>("[role=menuitem]"),
          ];
          let index = items.indexOf(document.activeElement as HTMLElement);
          index =
            e.key === "Home"
              ? 0
              : e.key === "End"
                ? items.length - 1
                : (index + (e.key === "ArrowDown" ? 1 : -1) + items.length) %
                  items.length;
          items[index]?.focus();
        }
      }}
    >
      <button
        ref={button}
        className={cn("avm-userbtn", open && "is-open")}
        aria-label="Korisnički izbornik"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Avatar name={user?.full_name ?? ""} />
        <span className="avm-userbtn__name">{user?.full_name}</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="avm-menu" role="menu" aria-label="Račun">
          <div className="avm-menu__head">
            <Avatar name={user?.full_name ?? ""} size={36} />
            <div>
              <div className="avm-menu__name">{user?.full_name}</div>
              <div className="avm-menu__email">{user?.email}</div>
            </div>
          </div>
          <div className="avm-menu__roles">
            {user?.roles.map((r) => (
              <span className="avm-badge avm-badge--neutral" key={r.code}>
                {r.name_hr}
              </span>
            ))}
          </div>
          {(
            [
              { label: "Profil", icon: "user", action: () => setProfile(true) },
              {
                label: "Promjena lozinke",
                icon: "key",
                action: () => navigate("/postavke/lozinka"),
              },
              {
                label: "Dvofaktorska prijava",
                icon: "shield",
                action: () => navigate("/postavke/dvofaktorska"),
              },
              { label: "Odjava", icon: "logOut", action: () => void logout() },
            ] as { label: string; icon: IconName; action: () => void }[]
          ).map((item) => (
            <button
              key={item.label}
              role="menuitem"
              className="avm-menu__item"
              onClick={() => {
                setOpen(false);
                item.action();
              }}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
      {profile && (
        <Dialog title="Profil" onClose={() => setProfile(false)}>
          <PropertyList>
            <PropertyItem label="Ime i prezime">{user?.full_name}</PropertyItem>
            <PropertyItem label="E-pošta">{user?.email}</PropertyItem>
            <PropertyItem label="Uloge">{user?.roles.map((r) => r.name_hr).join(", ")}</PropertyItem>
          </PropertyList>
        </Dialog>
      )}
    </div>
  );
}
function TopBar({ items, menuOpen, onMenuToggle }: { items: Stavka[]; menuOpen: boolean; onMenuToggle: () => void }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  const matches = items.filter((i) =>
    i.naziv.toLocaleLowerCase("hr").includes(query.toLocaleLowerCase("hr")),
  );
  return (
    <header className="avm-topbar" aria-label="Gornja traka">
      <button type="button" className="avm-mobile-menu-toggle" aria-label="Otvori izbornik" aria-expanded={menuOpen} aria-controls="app-navigation" onClick={onMenuToggle}>
        <span aria-hidden="true">☰</span>
      </button>
      <div className="avm-topbar__left">
        <div className="avm-global-search">
          <Icon name="search" />
          <input
            ref={ref}
            aria-label="Pretraži aplikaciju"
            placeholder="Pretraži aplikaciju"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
              if (!e.currentTarget.parentElement?.contains(e.relatedTarget))
                setFocused(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setFocused(false);
              if (e.key === "Enter" && matches[0]) {
                navigate(matches[0].putanja);
                setFocused(false);
                setQuery("");
              }
            }}
          />
          <kbd className="avm-kbd">Ctrl K</kbd>
          {focused && query && (
            <div className="avm-search-results">
              {matches.map((i) => (
                <NavLink
                  key={i.putanja}
                  end={i.putanja === "/oprema"}
                  to={i.putanja}
                  onClick={() => {
                    setQuery("");
                    setFocused(false);
                  }}
                >
                  <Icon name={i.icon} />
                  {i.naziv}
                </NavLink>
              ))}
              {!matches.length && <p>Nema pronađenih stranica.</p>}
            </div>
          )}
        </div>
      </div>
      <div className="avm-topbar__right">
        <SupportEntry />
        <ScanNotifications />
        <UserMenu />
      </div>
    </header>
  );
}
export function AppShell({
  children,
  desktopViewport = false,
}: {
  children: React.ReactNode;
  desktopViewport?: boolean;
}) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigation = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const nav = navigation.current;
    const background = [...(nav?.closest(".avm-shell")?.querySelectorAll<HTMLElement>(".avm-shell__main, .avm-shell > .app-navigation") ?? [])];
    background.forEach(element => { element.inert = true; });
    nav?.querySelector<HTMLElement>("button, a[href]")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setMenuOpen(false); }
      if (event.key !== "Tab" || !nav) return;
      const links = [...nav.querySelectorAll<HTMLElement>("button, a[href]")];
      const first = links[0], last = links.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const onResize = () => { if (window.innerWidth >= 768) setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => { document.removeEventListener("keydown", onKey); window.removeEventListener("resize", onResize); background.forEach(element => { element.inert = false; }); previous?.focus(); };
  }, [menuOpen]);
  const stavke = STAVKE.filter(
    (stavka) =>
      (stavka.putanja.startsWith("/nabava") && hasProcurement(user)) ||
      !stavka.ovlast ||
      (can(stavka.ovlast[0], stavka.ovlast[1]) &&
        (stavka.putanja !== "/postavke" || user?.permissions?.some(
          (p) => p.module === "auth" && p.action === "update" && p.scope === "all",
        ))) ||
      (stavka.putanja === "/postavke" && (can("sifrarnik", "update") || user?.permissions?.some(p => p.module === "radnik" && p.action === "update" && p.scope === "all"))) ||
      (stavka.putanja === "/obveze-tvrtke" &&
        user?.permissions?.some(
          (item) =>
            item.module === "company_document" &&
            item.action === "read" &&
            item.scope === "all",
        )) ||
      (stavka.putanja === "/adrese-za-prijavu" && can("dozvola", "read")) ||
      (stavka.putanja === "/ladica" && can("company_document", "read")) ||
      (stavka.putanja === "/ladica/unos" &&
        can("company_document", "upload")) ||
      (stavka.putanja === "/rokovi-i-obveze" &&
        (can("company_obligation", "read") ||
          can("obligation_task", "read"))) ||
      // Full employee-card access also permits opening the employee register.
      (stavka.putanja === "/zaposlenici" && can("radnik", "read")),
  );
  const jeVoditelj = user?.roles.some((r) => r.code === "voditelj") ?? false;
  const workerQuery = new URLSearchParams(location.search);
  const fixedDesktop =
    desktopViewport ||
    (/^\/zaposlenici\/[^/]+$/.test(location.pathname) &&
      workerDocumentWorkspace(workerQuery.get("tab") ?? "", workerQuery.get("dio"))) ||
    location.pathname === "/obveze-tvrtke" ||
    location.pathname === "/ladica";
  return (
    <HelpProvider>
    <NewsEntry key={user?.id} />
    <div className={cn("avm-shell", jeVoditelj && "avm-shell--manager", menuOpen && "is-menu-open")}>
      {menuOpen && <button type="button" className="avm-mobile-menu-backdrop" tabIndex={-1} aria-label="Zatvori izbornik" onClick={() => setMenuOpen(false)} />}
      <div className="avm-navigation-layer" role={menuOpen ? "dialog" : undefined} aria-modal={menuOpen ? true : undefined} aria-label={menuOpen ? "Izbornik aplikacije" : undefined}>
      <nav ref={navigation} id="app-navigation" aria-label="Glavni izbornik" className="avm-sidebar app-navigation" onClick={(event) => { if ((event.target as HTMLElement).closest("a")) setMenuOpen(false); }}>
        <button type="button" className="avm-mobile-menu-close" aria-label="Zatvori izbornik" onClick={() => setMenuOpen(false)}>Zatvori <Icon name="x" /></button>
        <div className="avm-sidebar__brand">
          <span className="avm-sidebar__mark">AVM</span>
          <span className="avm-sidebar__org">Poslovni sustav</span>
        </div>
        <div className="avm-sidebar__scroll">
          {[undefined, "Kadrovi", "Tvrtka", "Administracija", "support"].map((section) => (
            <div key={section ?? "main"} className="avm-sidebar__group">
              {section && section !== "support" && <div className="avm-sidebar__section">{section}</div>}
              {stavke
                .filter((s) => s.sekcija === section)
                .map((s) => (
                  <NavLink
                    key={s.putanja}
                    to={
                      s.putanja === "/ladica" && location.pathname === "/ladica"
                        ? inboxRootUrl(location.search)
                        : s.putanja
                    }
                    end={s.putanja === "/" || s.putanja === "/ladica" || s.putanja === "/oprema"}
                    aria-current={s.putanja === "/nabava" && ["/nabava/dobavljaci", "/nabava/ulazni-racuni"].some(prefix => location.pathname.startsWith(prefix)) ? false : "page"}
                    className={({ isActive }) =>
                      cn("avm-sidebar__item", isActive && (s.putanja !== "/nabava" || !["/nabava/dobavljaci", "/nabava/ulazni-racuni"].some(prefix => location.pathname.startsWith(prefix))) && "is-active")
                    }
                  >
                    <Icon name={s.icon} />
                    <span className="avm-sidebar__label">{s.naziv}</span>
                    {s.putanja === "/ladica" &&
                      can("dokument", "inbox_read") && <InboxNavBadge />}
                    {s.putanja === "/rokovi-i-obveze" && <ObligationNavBadge />}
                  </NavLink>
                ))}
              {section === "Tvrtka" && hasInvoicePermission(user) && (
                <button
                  type="button"
                  className={cn("avm-sidebar__item min-h-11 w-full text-left", location.pathname.startsWith("/nabava/ulazni-racuni") && "is-active")}
                  onClick={() => { setMenuOpen(false); navigate("/nabava/ulazni-racuni"); }}
                >
                  <Icon name="clipboard" />
                  <span className="avm-sidebar__label">Ulazni računi</span>
                </button>
              )}
              {section === "Tvrtka" &&
                (
                  [
                    ["truck", "Vozni park"],
                    ["cart", "Nabava"],
                  ] as [IconName, string][]
                ).filter(([, label]) => label !== "Nabava" || !hasProcurement(user)).map(([icon, label]) => (
                  <span
                    key={label}
                    className="avm-sidebar__item is-disabled"
                    aria-disabled="true"
                  >
                    <Icon name={icon} />
                    <span className="avm-sidebar__label">{label}</span>
                    <span className="avm-sidebar__soon">Uskoro</span>
                  </span>
                ))}
            </div>
          ))}
        </div>
      </nav>
      </div>
      <div className="avm-shell__main">
        <TopBar items={stavke} menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(!menuOpen)} />
        <main className={cn("avm-main", fixedDesktop && "avm-main--workspace")}>
          <div className="avm-system-notices">
            <TrakaReda />
            {jeVoditelj && <PonudaZaInstalaciju />}
          </div>
          {children}
          <WorkerTransferNotice />
        </main>
      </div>
      {jeVoditelj && (
        <nav
          className="app-navigation fixed inset-x-0 bottom-0 z-10 flex bg-nav md:hidden"
          aria-label="Glavni izbornik"
        >
          {stavke.map((s) => (
            <NavLink
              key={s.putanja}
              end={s.putanja === "/oprema"}
              to={
                s.putanja === "/ladica" && location.pathname === "/ladica"
                  ? inboxRootUrl(location.search)
                  : s.putanja
              }
              aria-current={s.putanja === "/nabava" && ["/nabava/dobavljaci", "/nabava/ulazni-racuni"].some(prefix => location.pathname.startsWith(prefix)) ? false : "page"}
              className="flex min-h-14 flex-1 flex-col items-center justify-center text-white"
            >
              <Icon name={s.icon} />
              <span className="text-xs">{s.naziv}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
    </HelpProvider>
  );
}
